import React, { useState, useMemo } from "react";
import { ArrowLeft, ChevronLeft, Loader } from "lucide-react";
import { RequestFormStepProps, RequestTimeData, TimeoffCategoryType } from "../../../../types";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { addDays } from "date-fns";
import { DateTimeUtil } from "../../../../utils";
import { toast } from "react-toastify";

const FormStep: React.FC<RequestFormStepProps> = ({
    formData,
    setFormData,
    selectedDates,
    onBack,
    onSubmit,
    isUpdateMode,
    timeoffToUpdate,
}) => {
    const { isCreatingTimeoffRequest, isUpdatingTimeoffRequest } = useSelector((state: RootState) => state.timeoff);
    const generateDateRange = (start: Date, end: Date): Date[] => {
        const dates: Date[] = [];
        let current = start;
        while (current <= end) {
            dates.push(new Date(current));
            current = addDays(current, 1);
        }
        return dates;
    };

    const fullDateRange = generateDateRange(selectedDates[0], selectedDates[selectedDates.length - 1]);

    const [timeData, setTimeData] = useState<RequestTimeData>(() =>
        fullDateRange.reduce((acc, date) => {
            const dateMysql = DateTimeUtil.formatDateToMySQL(date);
            acc[dateMysql] = { start_time: "", end_time: "", amount: "", date: dateMysql }; // Default to Full
            return acc;
        }, {} as RequestTimeData)
    );

    const timeoffCategories = useSelector((state: RootState) => state.common.assignedTimeoffCategories);
    const selectedCategory: TimeoffCategoryType | undefined = useMemo(
        () => timeoffCategories.find((cat) => cat.category_code === formData.category_code),
        [formData.category_code, timeoffCategories]
    );

    const totalTime = useMemo(() => {
        return Object.values(timeData)
            .reduce((acc, item) => acc + parseFloat(item.amount || "0"), 0) // Convert amount to number
            .toString(); // Convert back to string if needed for UI
    }, [timeData]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name === "category_code") {
            // Reset all time values when category changes
            setTimeData((prev) =>
                Object.fromEntries(Object.entries(prev).map(([key, val]) => [key, { ...val, amount: "0" }]))
            );
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        if (!formData.category_code) {
            // alert("Please select a category.");
            toast.error("Please select a category. If you cannot find the category, please contact your manager.", {
                position: "bottom-center",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            return;
        }

        if (!totalTime || Number(totalTime) === 0) {
            toast.error("Please enter a valid total time.", {
                position: "bottom-center",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            return;
        }

        const detailsArray = Object.entries(timeData).map(([date, { start_time, end_time, amount }]) => ({
            date,
            start_time: start_time || null,
            end_time: end_time || null,
            amount,
        }));

        setFormData((prev) => ({ ...prev, details: detailsArray }));
        onSubmit({ ...formData, details: detailsArray });
    };

    const handleDurationChange = (dateKey: string, value: string) => {
        // If the input is empty, reset to "0"
        if (value === "") {
            setTimeData((prev) => ({
                ...prev,
                [dateKey]: { ...prev[dateKey], amount: "0" },
            }));
            return;
        }

        // Remove leading zeros if present
        let sanitizedValue = value.replace(/^0+(\d)/, "$1");

        // Ensure it is a valid numeric input
        if (/^\d*\.?\d*$/.test(sanitizedValue)) {
            const numericValue = parseFloat(sanitizedValue);

            // Ensure it's not more than 24
            if (numericValue > 24) return;
            setTimeData((prev) => ({
                ...prev,
                [dateKey]: { ...prev[dateKey], amount: sanitizedValue },
            }));
        }
    };

    return (
        <div>
            {/* <div className="relative flex items-center justify-center mb-4">
                <button
                    aria-label="Back"
                    className="text-gray-700 hover:text-black absolute left-4"
                    onClick={onBack}
                >
                    <ArrowLeft className="w-6 h-6" />
                </button>
                <h2 className="text-xl font-semibold">New Request</h2>
            </div> */}

            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-800 mb-2">
                    <span className="text-red-500 text-lg">*</span> Category
                </label>
                <select
                    name="category_code"
                    value={formData.category_code || ""}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white appearance-none"
                >
                    <option value="">--Select--</option>
                    {timeoffCategories.map((category) => (
                        <option key={category.category_code} value={category.category_code}>
                            {category.category_name}
                        </option>
                    ))}
                </select>
                {/* {!formData.category_code && <p className="text-red-500 text-sm mt-1">Category is required.</p>} */}
            </div>

            {selectedCategory?.track_time_in && (
                <div>
                    <div className="flex justify-between items-center text-gray-700 font-medium mb-2 text-sm">
                        <span>
                            <span className="text-red-500 text-lg">*</span> {selectedCategory?.track_time_in ?? ""}
                        </span>
                        <span>Total: {totalTime}</span>
                    </div>

                    <div className="border border-gray-200 rounded-md">
                        {fullDateRange.map((date) => {
                            const dateKey = DateTimeUtil.formatDateToMySQL(date);
                            return (
                                <div
                                    key={dateKey}
                                    className="flex items-center justify-between px-3 py-3 border-b border-gray-200"
                                >
                                    <span className="text-md font-medium text-gray-700">
                                        {DateTimeUtil.formatShortDate(date)}
                                    </span>

                                    {selectedCategory?.track_time_in === "Hours" ? (
                                        <input
                                            type="text"
                                            value={timeData[dateKey]?.amount.toString()} // Ensure it's a string
                                            onChange={(e) => handleDurationChange(dateKey, e.target.value)}
                                            className="border border-gray-300 rounded-md px-2 py-1 w-20 text-center bg-white"
                                            inputMode="decimal"
                                        />
                                    ) : (
                                        <select
                                            value={timeData[dateKey]?.amount.toString()}
                                            onChange={(e) => handleDurationChange(dateKey, e.target.value)}
                                            className="border border-gray-300 rounded-md px-2 py-1 w-20 text-center bg-white appearance-none"
                                        >
                                            <option value="">--Select--</option>
                                            <option value="1">Full</option>
                                            <option value="0.5">Half</option>
                                        </select>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="mt-4">
                <label className="block text-sm font-medium text-gray-800 mb-2">Note</label>
                <textarea
                    name="note"
                    value={formData.note || ""}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white"
                    placeholder="Add a note (optional)"
                />
            </div>

            {isCreatingTimeoffRequest || isUpdatingTimeoffRequest ? (
                <div className="flex justify-center items-center">
                    <Loader className="animate-spin w-6 h-6 text-gray-800" />
                </div>
            ) : (
                <button
                    className="w-full px-4 py-2 mt-4 bg-gray-800 text-white rounded-md hover:bg-gray-900"
                    onClick={handleSubmit}
                >
                    Submit
                </button>
            )}
        </div>
    );
};

export default FormStep;
